import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOffice } from '@/hooks/useOffice';
import OfficeSwitcher from '@/components/OfficeSwitcher';

interface Tape {
  id: number;
  media_label: string;
  media_set: string | null;
  modified_date: string | null;
  tape_type: string | null;
  location: string | null;
  box_number: string | null;
  backup_set: string | null;
  needs_replacement?: boolean;
  damaged?: boolean;
  updated_at: string;
}

export default function Dashboard() {
  const { currentOfficeId, currentOffice } = useOffice();
  const [tapes, setTapes] = useState<Tape[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [backupSetQuery, setBackupSetQuery] = useState('');
  const [backupSearchText, setBackupSearchText] = useState('');
  const [backupSearchOpen, setBackupSearchOpen] = useState(false);
  const backupSearchRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    if (!currentOfficeId) return;
    setLoading(true); setError('');
    try {
      const { data, error } = await supabase.from('tapes').select('*').eq('office_id', currentOfficeId).order('updated_at', { ascending: false });
      if (error) throw error;
      setTapes(data as Tape[]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [currentOfficeId]);

  const backupSets = useMemo(
    () => Array.from(new Set(tapes.map((t) => t.backup_set).filter(Boolean))).sort(),
    [tapes],
  );
  const filteredBackupSets = backupSets.filter(
    (o) => !backupSearchText || o.toLowerCase().includes(backupSearchText.toLowerCase()),
  );

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (backupSearchRef.current && !backupSearchRef.current.contains(e.target as Node)) {
        setBackupSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const total = tapes.length;
  const byType = tapes.reduce<Record<string, number>>((a, t) => {
    const k = t.media_set || 'Unknown';
    a[k] = (a[k] || 0) + 1; return a;
  }, {});
  const byLocation = tapes.reduce<Record<string, number>>((a, t) => {
    const k = t.location || 'Unknown';
    a[k] = (a[k] || 0) + 1; return a;
  }, {});
  const inWarehouse = byLocation['Warehouse'] || 0;
  const inOffice = byLocation['Shanghai Office'] || 0;
  const inLibrary = tapes.filter((t) => t.box_number === 'Backup Tape Library').length;
  const needsReplacement = tapes.filter((t) => t.needs_replacement).length;
  const scrapped = tapes.filter((t) => t.damaged).length;
  const filteredTapes = backupSetQuery ? tapes.filter((t) => t.backup_set === backupSetQuery) : tapes;
  const recent = filteredTapes.slice(0, 8);

  const stats = [
    { label: 'Total Tapes', value: total, icon: 'ri-hard-drive-2-line', color: 'bg-slate-900', to: '/tapes' },
    { label: 'In Warehouse', value: inWarehouse, icon: 'ri-archive-line', color: 'bg-amber-600', to: '/tapes?location=Warehouse' },
    { label: 'In Office', value: inOffice, icon: 'ri-building-2-line', color: 'bg-emerald-600', to: '/tapes?location=Shanghai Office' },
    { label: 'In Tape Library', value: inLibrary, icon: 'ri-database-2-line', color: 'bg-indigo-600', to: '/library' },
    { label: 'Tape Replacement', value: needsReplacement, icon: 'ri-refresh-line', color: 'bg-rose-600', to: '/replacement' },
    { label: 'Scrapped Tapes', value: scrapped, icon: 'ri-error-warning-line', color: 'bg-red-600', to: '/damaged' },
  ];

  return (
    <div className="p-8">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Overview of your backup tape library.</p>
        </div>
        <div ref={backupSearchRef} className="relative w-full md:w-80">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 flex items-center justify-center" />
          <input
            value={backupSearchText}
            onChange={(e) => { setBackupSearchText(e.target.value); setBackupSearchOpen(true); }}
            onFocus={() => setBackupSearchOpen(true)}
            placeholder="Search tapes by Backup Set..."
            className="w-full pl-9 pr-9 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:border-slate-900"
          />
          {backupSearchText && (
            <button
              onClick={() => { setBackupSearchText(''); setBackupSetQuery(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
            >
              <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
            </button>
          )}
          {backupSearchOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md z-30 max-h-60 overflow-auto">
              {filteredBackupSets.map((o) => (
                <button
                  key={o}
                  onClick={() => { setBackupSetQuery(o); setBackupSearchText(o); setBackupSearchOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                >
                  {o}
                </button>
              ))}
              {filteredBackupSets.length === 0 && (
                <div className="px-3 py-2 text-sm text-slate-400">No matching Backup Set</div>
              )}
              {backupSetQuery && (
                <button
                  onClick={() => { setBackupSetQuery(''); setBackupSearchText(''); setBackupSearchOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm text-slate-500 border-t border-slate-100 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                >
                  Clear filter
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Office switcher / creator */}
      <div className="mb-6 bg-white rounded-lg border border-slate-200 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 shrink-0">
              <i className="ri-building-2-line w-6 h-6 flex items-center justify-center" />
            </div>
            <div>
              <div className="text-xs text-slate-500">Current office</div>
              <div className="text-lg font-semibold">{currentOffice?.name ?? '—'}</div>
              <div className="text-xs text-slate-400 mt-0.5">
                All data, imports and backups are scoped to this office.
              </div>
            </div>
          </div>
          <div className="w-full sm:w-56">
            <OfficeSwitcher />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer">Retry</button>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} className="block bg-white rounded-lg border border-slate-200 p-5 hover:border-slate-300 hover:bg-slate-50 transition-colors cursor-pointer">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500">{s.label}</div>
                <div className="text-2xl font-semibold mt-1">{loading ? '—' : s.value}</div>
              </div>
              <div className={`w-10 h-10 flex items-center justify-center rounded-md text-white ${s.color}`}>
                <i className={`${s.icon} w-5 h-5 flex items-center justify-center`} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold mb-4">By Media Set</h3>
          <div className="space-y-2">
            {Object.entries(byType).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k, v]) => {
              const pct = total ? Math.round((v / total) * 100) : 0;
              return (
                <div key={k}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-700">{k}</span>
                    <span className="text-slate-500">{v} · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-900" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-5">
          <h3 className="text-sm font-semibold mb-4">By Location</h3>
          <div className="space-y-2">
            {Object.entries(byLocation).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
              const pct = total ? Math.round((v / total) * 100) : 0;
              return (
                <div key={k}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-700">{k}</span>
                    <span className="text-slate-500">{v} · {pct}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-600" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold">
            {backupSetQuery ? `Tapes in "${backupSetQuery}"` : 'Recently Updated'}
          </h3>
          <Link to="/tapes" className="text-xs text-slate-500 hover:text-slate-900 cursor-pointer">View all →</Link>
        </div>
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-5 py-2.5 font-medium">Media Label</th>
              <th className="text-left px-5 py-2.5 font-medium">Media Set</th>
              <th className="text-left px-5 py-2.5 font-medium">Backup Set</th>
              <th className="text-left px-5 py-2.5 font-medium">Tape Type</th>
              <th className="text-left px-5 py-2.5 font-medium">Location</th>
              <th className="text-left px-5 py-2.5 font-medium">Box</th>
              <th className="text-left px-5 py-2.5 font-medium">Modified</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-2.5 font-medium">{t.media_label}</td>
                <td className="px-5 py-2.5 text-slate-600">{t.media_set || '—'}</td>
                <td className="px-5 py-2.5 text-slate-600">{t.backup_set || '—'}</td>
                <td className="px-5 py-2.5 text-slate-600">{t.tape_type || '—'}</td>
                <td className="px-5 py-2.5 text-slate-600">{t.location || '—'}</td>
                <td className="px-5 py-2.5 text-slate-600 text-xs">{t.box_number || '—'}</td>
                <td className="px-5 py-2.5 text-slate-600 text-xs">{t.modified_date || '—'}</td>
              </tr>
            ))}
            {!loading && recent.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">No tapes yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}